import 'css/src/components/public-pages/contentful-components/audio-sync/AudioTrackTimeline';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import AudioTrackItem from './AudioTrackItem';
import type { AudioTrack } from './useMediaBunnyMixer';

import { getTimeStringFromMillis } from 'js/src/components/common/asset-trimmer/common';

interface AudioTrackTimelineProps {
    videoDurationMs: number;
    videoFile: File | null;
    audioTracks: AudioTrack[];
    currentTimeMs: number;
    isPlaying: boolean;
    zoomLevel: number;
    onAudioTrackOffsetChange: (id: string, offsetMs: number) => void;
    onAudioTrackRemove: (id: string) => void;
    onVideoUploadClick: () => void;
    onVideoClear: () => void;
    onAudioUploadClick: () => void;
    onAudioDrop: (files: FileList) => void;
    onSeek: (timeMs: number) => void;
}

/**
 * Generates time markers for the timeline, including negative times if needed
 * @param startMs - Start of timeline in ms (can be negative)
 * @param endMs - End of timeline in ms
 * @param visibleDurationMs - The duration visible on screen (accounts for zoom)
 */
function generateTimeMarkers(startMs: number, endMs: number, visibleDurationMs: number): number[] {
    const markers: number[] = [];

    if (endMs <= startMs) {
        return [0];
    }

    // Target ~10 markers on screen
    const targetMarkerCount = 10;
    const visibleDurationSec = visibleDurationMs / 1000;

    const idealIntervalSec = visibleDurationSec / targetMarkerCount;

    const niceIntervals = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
    let intervalSec = niceIntervals[0];
    for (const nice of niceIntervals) {
        if (nice >= idealIntervalSec) {
            intervalSec = nice;
            break;
        }
        intervalSec = nice;
    }

    // For very short durations (< 10 seconds visible), use sub-second intervals
    if (visibleDurationSec < 10) {
        intervalSec = Math.max(1, Math.ceil(visibleDurationSec / targetMarkerCount));
    }

    // Start from a round number at or before startMs
    const startSec = Math.floor(startMs / 1000);
    const firstMarkerSec = Math.floor(startSec / intervalSec) * intervalSec;

    for (let sec = firstMarkerSec; sec * 1000 <= endMs; sec += intervalSec) {
        markers.push(sec * 1000);
    }

    // Always include 0 if it's in range and not already included
    if (startMs <= 0 && endMs >= 0 && !markers.includes(0)) {
        markers.push(0);
        markers.sort((a, b) => a - b);
    }

    return markers;
}

const AudioTrackTimeline: React.FC<AudioTrackTimelineProps> = ({
    videoDurationMs,
    videoFile,
    audioTracks,
    currentTimeMs,
    isPlaying,
    zoomLevel,
    onAudioTrackOffsetChange,
    onAudioTrackRemove,
    onVideoUploadClick,
    onVideoClear,
    onAudioUploadClick,
    onAudioDrop,
    onSeek,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(800);
    const [isAudioDragOver, setIsAudioDragOver] = useState(false);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const scrubberRef = useRef<HTMLDivElement>(null);
    const prevZoomLevelRef = useRef(zoomLevel);

    // Base video duration (for scale calculation - stays fixed)
    const effectiveVideoDuration = videoDurationMs > 0 ? videoDurationMs : 30000;

    const { timelineStartMs, timelineEndMs, totalDurationMs } = useMemo(() => {
        // Timeline always starts at the earliest offset for negative offsets or 0
        let minOffset = 0;
        let maxEnd = effectiveVideoDuration;

        audioTracks.forEach((track) => {
            if (track.offsetMs < minOffset) {
                minOffset = track.offsetMs;
            }
            const trackEnd = track.offsetMs + track.durationMs;
            if (trackEnd > maxEnd) {
                maxEnd = trackEnd;
            }
        });

        const startMs = Math.min(minOffset, 0);
        const endMs = maxEnd;
        const duration = endMs - startMs;

        return {
            timelineStartMs: startMs,
            timelineEndMs: endMs,
            totalDurationMs: duration,
        };
    }, [effectiveVideoDuration, audioTracks]);

    const baseTimelineWidth = containerWidth * zoomLevel;

    useEffect(() => {
        // Maintain center position when zoom changes
        const prevZoom = prevZoomLevelRef.current;
        if (prevZoom !== zoomLevel && scrollContainerRef.current) {
            const scrollContainer = scrollContainerRef.current;
            const scrollLeft = scrollContainer.scrollLeft;
            const visibleWidth = scrollContainer.clientWidth;

            // Calculate the time at the center before zoom change
            const prevBaseTimelineWidth = containerWidth * prevZoom;
            const prevMsPerPixel = effectiveVideoDuration / prevBaseTimelineWidth;
            const centerPixelBefore = scrollLeft + visibleWidth / 2;
            const centerTimeMs = timelineStartMs + centerPixelBefore * prevMsPerPixel;

            // Calculate new scroll position to keep that time centered
            const newMsPerPixel = effectiveVideoDuration / baseTimelineWidth;
            const centerPixelAfter = (centerTimeMs - timelineStartMs) / newMsPerPixel;
            const newScrollLeft = centerPixelAfter - visibleWidth / 2;

            scrollContainer.scrollLeft = Math.max(0, newScrollLeft);
        }
        prevZoomLevelRef.current = zoomLevel;
    }, [zoomLevel, containerWidth, effectiveVideoDuration, baseTimelineWidth, timelineStartMs]);

    const handleAudioDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (videoFile) {
                setIsAudioDragOver(true);
            }
        },
        [videoFile],
    );

    const handleAudioDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsAudioDragOver(false);
    }, []);

    const handleAudioDropEvent = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsAudioDragOver(false);

            if (videoFile && e.dataTransfer.files.length > 0) {
                onAudioDrop(e.dataTransfer.files);
            }
        },
        [videoFile, onAudioDrop],
    );

    useEffect(() => {
        if (!containerRef.current) return;

        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const visibleDurationMs = effectiveVideoDuration / zoomLevel;

    const timeMarkers = useMemo(
        () => generateTimeMarkers(timelineStartMs, timelineEndMs, visibleDurationMs),
        [timelineStartMs, timelineEndMs, visibleDurationMs],
    );

    // msPerPixel is based on VIDEO duration, not total timeline duration
    // This keeps the scale fixed - audio extending beyond video won't shrink things
    const msPerPixel = effectiveVideoDuration / baseTimelineWidth;

    // Calculate the actual timeline content width based on total duration
    // This may be wider than the container when audio extends beyond video
    const actualTimelineWidth = totalDurationMs / msPerPixel;

    // Calculate position of 0 on the timeline (for video track positioning)
    const zeroPositionPx = Math.abs(timelineStartMs) / msPerPixel;

    // Calculate the video bar width (based on actual video duration)
    const videoBarWidthPx = effectiveVideoDuration / msPerPixel;

    // Calculate playhead position
    const playheadPositionPx = (currentTimeMs - timelineStartMs) / msPerPixel;

    const calculateTimeFromPosition = useCallback(
        (clientX: number) => {
            if (!containerRef.current || !scrollContainerRef.current) return 0;
            const rect = containerRef.current.getBoundingClientRect();
            const scrollLeft = scrollContainerRef.current.scrollLeft;
            const x = clientX - rect.left + scrollLeft;
            const timeMs = timelineStartMs + x * msPerPixel;
            // Clamp to video duration (0 to videoDurationMs)
            return Math.max(0, Math.min(videoDurationMs, timeMs));
        },
        [timelineStartMs, msPerPixel, videoDurationMs],
    );

    const handleScrubberMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsScrubbing(true);
            const timeMs = calculateTimeFromPosition(e.clientX);
            onSeek(timeMs);
        },
        [calculateTimeFromPosition, onSeek],
    );

    useEffect(() => {
        // Handle scrubbing (mouse move while dragging)
        if (!isScrubbing) return;

        const handleMouseMove = (e: MouseEvent) => {
            const timeMs = calculateTimeFromPosition(e.clientX);
            onSeek(timeMs);
        };

        const handleMouseUp = () => {
            setIsScrubbing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isScrubbing, calculateTimeFromPosition, onSeek]);

    const handleTimelineClick = useCallback(
        (e: React.MouseEvent) => {
            // Don't seek if clicking on a track or button
            if ((e.target as HTMLElement).closest('.audio-track-item, button')) {
                return;
            }
            const timeMs = calculateTimeFromPosition(e.clientX);
            onSeek(timeMs);
        },
        [calculateTimeFromPosition, onSeek],
    );

    return (
        <div className="audio-track-timeline-wrapper w-100 position-relative" ref={containerRef}>
            <div className="audio-track-timeline-scroll w-100" ref={scrollContainerRef}>
                <div
                    className="audio-track-timeline position-relative  mt-l5-24"
                    onClick={handleTimelineClick}
                    style={{
                        cursor: videoFile ? 'pointer' : 'default',
                        width: `${actualTimelineWidth}px`,
                    }}
                >
                    {videoFile && videoDurationMs > 0 && (
                        <div
                            ref={scrubberRef}
                            className={classNames(
                                'timeline-playhead position-absolute top-0 bottom-0',
                                {
                                    scrubbing: isScrubbing,
                                    playing: isPlaying,
                                },
                            )}
                            style={{ left: `${playheadPositionPx}px` }}
                            onMouseDown={handleScrubberMouseDown}
                        >
                            <div className="playhead-handle" />
                            <div className="playhead-tooltip">
                                {getTimeStringFromMillis(currentTimeMs)}
                            </div>
                        </div>
                    )}

                    <div className="timeline-ruler position-relative">
                        {timeMarkers.map((ms) => {
                            const left = (ms - timelineStartMs) / msPerPixel;
                            const isZero = ms === 0;
                            return (
                                <div
                                    key={ms}
                                    className="timeline-marker"
                                    style={{ left: `${left}px` }}
                                >
                                    <div
                                        className={`marker-tick ${isZero ? 'marker-tick--zero' : ''}`}
                                    />
                                    <span
                                        className={`marker-label mt-l5-2 ${isZero ? 'marker-label--zero' : ''}`}
                                    >
                                        {getTimeStringFromMillis(ms)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="video-track-row position-relative d-flex align-items-center">
                        {videoFile ? (
                            <>
                                {timelineStartMs < 0 && (
                                    <div
                                        className="video-negative-area"
                                        style={{ width: `${zeroPositionPx}px` }}
                                    />
                                )}
                                <div
                                    className="video-track-item d-flex flex-column"
                                    style={{
                                        left: `${zeroPositionPx}px`,
                                        width: `${videoBarWidthPx}px`,
                                    }}
                                >
                                    <div className="video-track-header d-flex justify-content-between align-items-center">
                                        <span className="video-track-name d-flex align-items-center">
                                            <i className="material-symbols-outlined">movie</i>
                                            {videoFile.name}
                                        </span>
                                        <button
                                            onClick={onVideoClear}
                                            type="button"
                                            className="video-track-remove-btn"
                                        >
                                            <i className="material-symbols-outlined">close</i>
                                        </button>
                                    </div>
                                    <div className="video-track-body" />
                                </div>
                            </>
                        ) : (
                            <button
                                onClick={onVideoUploadClick}
                                type="button"
                                className="video-upload-btn w-100 d-flex align-items-center justify-content-center"
                            >
                                <i className="material-symbols-outlined">movie</i>
                                <span>Click to add video</span>
                            </button>
                        )}
                    </div>

                    <div
                        className={`audio-tracks-container position-relative ${isAudioDragOver ? 'drag-over' : ''}`}
                        style={{
                            minHeight:
                                audioTracks.length > 0
                                    ? `${audioTracks.length * 60 + 60}px`
                                    : '100px',
                        }}
                        onDragOver={handleAudioDragOver}
                        onDragLeave={handleAudioDragLeave}
                        onDrop={handleAudioDropEvent}
                    >
                        {isAudioDragOver && (
                            <div className="audio-drop-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                                <div className="audio-drop-content d-flex flex-column align-items-center">
                                    <i
                                        className="material-symbols-outlined"
                                        style={{ fontSize: '32px' }}
                                    >
                                        music_note
                                    </i>
                                    <span>Drop audio files here</span>
                                </div>
                            </div>
                        )}

                        {timelineStartMs < 0 && (
                            <div
                                className="audio-negative-area"
                                style={{ width: `${zeroPositionPx}px` }}
                            />
                        )}

                        {timeMarkers.map((ms) => {
                            const left = (ms - timelineStartMs) / msPerPixel;
                            const isZero = ms === 0;
                            return (
                                <div
                                    key={ms}
                                    className={`audio-grid-line ${isZero ? 'audio-grid-line--zero' : ''}`}
                                    style={{ left: `${left}px` }}
                                />
                            );
                        })}

                        {audioTracks.map((track, index) => (
                            <div key={track.id} className="audio-track-wrapper position-relative">
                                <div className="audio-track-label">Audio {index + 1}</div>
                                <AudioTrackItem
                                    track={track}
                                    totalDurationMs={totalDurationMs}
                                    timelineWidth={actualTimelineWidth}
                                    timelineStartMs={timelineStartMs}
                                    onOffsetChange={onAudioTrackOffsetChange}
                                    onRemove={onAudioTrackRemove}
                                />
                            </div>
                        ))}

                        <div
                            className="audio-add-row position-relative"
                            style={{ marginTop: audioTracks.length > 0 ? '0' : '10px' }}
                        >
                            <button
                                onClick={onAudioUploadClick}
                                type="button"
                                disabled={!videoFile}
                                className="audio-upload-btn w-100 h-100 d-flex align-items-center justify-content-center"
                            >
                                <i className="material-symbols-outlined">music_note</i>
                                <span>
                                    {videoFile
                                        ? 'Click to add audio file(s)'
                                        : 'Add video first to add audio'}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(AudioTrackTimeline);
